import { memo, useCallback } from "react";
import { useShallow } from "zustand/react/shallow";

import TableSimple from "ds/components/TableSimple";
import { Stack } from "types/generated";
import Box from "ds/components/Box";
import useBulkActionsFloatingBarHeight from "components/BulkActions/FloatingBar/hooks/useBulkActionsFloatingBarHeight";
import { selectSelectionIsStarted } from "ds/components/Table/store/useTableSelectionStore/selectors";
import useShiftSelectionManager from "components/BulkActions/useShiftSelectionManager";

import useStacksTableStore from "./useStacksTableStore";
import { STACKS_TABLE_CELLS } from "./Cells";
import useStackListAnalytics from "../useStackListAnalytics";
import { ANALYTICS, FILTERS_ORDER_SETTINGS_KEY, SORT_CONFIG } from "../constants";
import useStacksTableSelectionStore from "./useStacksTableSelectionStore";
import { useStacksSelectionOptions } from "./useStacksSelectionOptions";
import StacksBulkActions from "../BulkActions";

type StacksTableProps = {
  stacks: Stack[];
  loading: boolean;
  refetchVisibleStacks: () => Promise<unknown>;
};

const StacksTable = ({ stacks, loading, refetchVisibleStacks }: StacksTableProps) => {
  const trackAnalytics = useStackListAnalytics();

  const { selectionOptions, stacksMap } = useStacksSelectionOptions(stacks);
  const {
    isBulkActionsActive,
    toggle,
    clearSelected,
    selectedSet,
    selectionLoading,
    replaceSelected,
    refetchSelected,
  } = useStacksTableSelectionStore(
    useShallow((state) => ({
      isBulkActionsActive: selectSelectionIsStarted(state),
      toggle: state.toggle,
      clearSelected: state.clearSelected,
      selectedSet: state.selected,
      selectionLoading: state.loading,
      replaceSelected: state.replaceSelected,
      refetchSelected: state.refetchSelected,
    }))
  );

  const onBulkContinueWith = useCallback(
    (set: Set<string>) => replaceSelected(Array.from(set)),
    [replaceSelected]
  );

  const { height: floatingBarHeight, setHeight: setFloatingBarHeight } =
    useBulkActionsFloatingBarHeight(isBulkActionsActive);

  const shiftSelectionAction = useShiftSelectionManager({
    collection: stacks,
    selectedIds: selectedSet,
  });

  const onBulkActionsFinish = useCallback(async () => {
    refetchVisibleStacks();
    refetchSelected?.();
  }, [refetchSelected, refetchVisibleStacks]);

  return (
    <>
      <TableSimple
        data={stacks}
        loading={loading}
        store={useStacksTableStore}
        selectionStore={useStacksTableSelectionStore}
        selectionOptions={selectionOptions}
        shiftSelectionAction={shiftSelectionAction}
        ariaLabel="Stacks table"
        cellPerColumn={STACKS_TABLE_CELLS}
        trackAnalytics={trackAnalytics}
        filtersStorageKey={FILTERS_ORDER_SETTINGS_KEY}
        analyticsPage={ANALYTICS.analyticsPage}
        sortConfig={SORT_CONFIG}
        bottomOffset={floatingBarHeight}
        virtualize={stacks.length >= 50}
      />

      <Box fullWidth relative>
        <StacksBulkActions
          selectedSet={selectedSet}
          stacksMap={stacksMap}
          onBulkResetAll={clearSelected}
          onBulkContinueWith={onBulkContinueWith}
          onItemDismiss={toggle}
          onFinish={onBulkActionsFinish}
          isLoadingItems={selectionLoading}
          onHeightChange={setFloatingBarHeight}
        />
      </Box>
    </>
  );
};

export default memo(StacksTable);
